import { UtilService } from '@shared/services/util.service';

/**
 * Parcelas de `total` proporcionais às `bases`, na mesma ordem, pela diferença dos acumulados arredondados: a parcela
 * do item é o arredondado em `casas` (ABNT, `UtilService.arredondarAbnt`) de total × bases acumuladas até ele ÷ soma
 * das bases, menos o mesmo acumulado até o item anterior. Nenhuma parcela fica negativa e o ÚLTIMO item fecha a soma
 * no total arredondado nas mesmas casas. Total zero devolve zeros. Base negativa, ou soma das bases zero com total
 * diferente de zero, é erro: não há proporção para ratear (quem chama decide o que mostrar nesse caso).
 * O ruído de ponto flutuante é tirado com `toPrecision(15)` antes de arredondar.
 * Par no servidor: ATS.Hub.Lib.Shared/Helpers/RateioValorHelper.cs. A função @rateio do motor de cálculo segue a
 * mesma conta (fechamento no último item).
 */
export function ratearProporcional(bases: number[], total: number, casas: number): number[] {
  if (bases.some(base => base < 0)) throw new Error('O rateio não aceita base negativa.');

  const arredondar = (valor: number): number => UtilService.arredondarAbnt(Number(valor.toPrecision(15)), casas);
  const totalArredondado = arredondar(total);
  if (totalArredondado === 0) return bases.map(() => 0);

  const somaBases = bases.reduce((soma, base) => soma + base, 0);
  if (somaBases === 0) throw new Error('O rateio precisa de bases com valor para distribuir um total diferente de zero.');

  let baseAcumulada = 0;
  let distribuido = 0;
  return bases.map((base, i) => {
    baseAcumulada += base;
    const acumuladoArredondado = i === bases.length - 1
      ? totalArredondado
      : arredondar((totalArredondado * baseAcumulada) / somaBases);
    const parcela = arredondar(acumuladoArredondado - distribuido);
    distribuido = acumuladoArredondado;
    return parcela;
  });
}
